import { z } from 'zod';

// ============================================================================
// Dashboard Metrics Schemas
// ============================================================================

/**
 * Schema for dashboard metrics data
 * 
 * Note: failed_validations represents verifications that were rejected
 * due to not meeting configured security requirements, not technical failures.
 */
export const DashboardMetricsSchema = z.object({
  total_validations: z.number().int().min(0),
  successful_validations: z.number().int().min(0),
  failed_validations: z.number().int().min(0), // Rejected by security requirements
  pending_validations: z.number().int().min(0),
  validation_rate: z.number().min(0).max(100),
  unique_devices: z.number().int().min(0),
  approved_devices: z.number().int().min(0),
  device_conversion_rate: z.number().min(0).max(100),
  period: z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
  }),
});

export type DashboardMetrics = z.infer<typeof DashboardMetricsSchema>;

// ============================================================================
// New Chart Data Schemas
// ============================================================================

/**
 * Schema for pending verification steps breakdown
 */
export const PendingStepsDataSchema = z.object({
  data: z.array(z.object({
    name: z.string(),
    value: z.number().int().min(0),
  })),
  title: z.string(),
  type: z.literal('bar'),
});

export type PendingStepsData = z.infer<typeof PendingStepsDataSchema>;

/**
 * Schema for device usage data
 */
export const DeviceDataSchema = z.object({
  data: z.array(z.object({
    // Display name (truncated for visual layout).
    name: z.string(),
    value: z.number().int().min(0),
    // Untruncated original used as the cross-filter value. Optional so
    // the schema stays backwards compat with consumers that don't read it.
    rawName: z.string().optional(),
  })),
  title: z.string(),
  type: z.literal('pie'),
});

export type DeviceData = z.infer<typeof DeviceDataSchema>;

/**
 * Schema for browser usage data
 */
export const BrowserDataSchema = z.object({
  data: z.array(z.object({
    name: z.string(),
    value: z.number().int().min(0),
  })),
  title: z.string(),
  type: z.literal('pie'),
});

export type BrowserData = z.infer<typeof BrowserDataSchema>;

/**
 * Schema for validation duration percentiles
 */
export const DurationPercentilesDataSchema = z.object({
  data: z.array(z.object({
    name: z.string(),
    value: z.number().min(0),
  })),
  title: z.string(),
  type: z.literal('bar'),
  unit: z.string(),
});

export type DurationPercentilesData = z.infer<typeof DurationPercentilesDataSchema>;

/**
 * Schema for SDK version distribution data.
 *
 * Each row represents a (platform, version) pair with its observed count.
 * `platform` is the raw value as emitted by the SDK (e.g. "ANDROID", "IOS",
 * "WEB"); the UI maps it for display. Rows already exclude empty/"Unknown"
 * versions on the backend so this list is safe to render directly.
 */
export const SdkVersionEntrySchema = z.object({
  platform: z.string(),
  version: z.string(),
  count: z.number().int().min(0),
});

export type SdkVersionEntry = z.infer<typeof SdkVersionEntrySchema>;

/**
 * Schema for Document Type distribution data.
 *
 * Each row is one document type id (e.g. "ARG_3", "ARG_4", "COL_3") with
 * its observed count. The SDK emits these as fixed identifiers — the
 * dashboard renders them verbatim because the spelling/casing is the
 * authoritative key the backend filter exact-matches against.
 */
export const IdTypeEntrySchema = z.object({
  idType: z.string(),
  count: z.number().int().min(0),
});

export type IdTypeEntry = z.infer<typeof IdTypeEntrySchema>;

// ============================================================================
// Chart Data Schemas
// ============================================================================

/**
 * Schema for chart dataset
 */
export const DatasetSchema = z.object({
  label: z.string(),
  data: z.array(z.number()),
  backgroundColor: z.array(z.string()).optional(),
  borderColor: z.string().optional(),
  borderWidth: z.number().optional(),
});

export type Dataset = z.infer<typeof DatasetSchema>;

/**
 * Schema for chart data structure
 */
export const ChartDataSchema = z.object({
  labels: z.array(z.string()),
  datasets: z.array(DatasetSchema),
});

export type ChartData = z.infer<typeof ChartDataSchema>;

/**
 * Schema for pie chart data item
 */
export const PieChartItemSchema = z.object({
  name: z.string(),
  value: z.number().min(0),
});

export type PieChartItem = z.infer<typeof PieChartItemSchema>;

/**
 * Schema for pie chart data
 */
export const PieChartDataSchema = z.object({
  data: z.array(PieChartItemSchema),
  title: z.string(),
  legend: z.array(z.string()),
  type: z.string().default('pie'),
  radius: z.array(z.string()).nullable().optional(),
});

export type PieChartData = z.infer<typeof PieChartDataSchema>;

// ============================================================================
// Geographic Data Schemas
// ============================================================================

/**
 * Schema for geographic coordinates
 */
export const CoordinatesSchema = z.object({
  lat: z.number().min(-90).max(90).finite(),
  lng: z.number().min(-180).max(180).finite(),
});

export type Coordinates = z.infer<typeof CoordinatesSchema>;

/**
 * Schema for geographic data (matching original Nuxt Map component expectations)
 */
export const GeographicDataSchema = z.object({
  id: z.string().min(1),
  coordinates: CoordinatesSchema,
  country: z.string().min(1).nullable().optional(),
  country_code: z.string().length(2).nullable().optional(),
  validations: z.number().int().min(0).nullable().optional(),
});

export type GeographicData = z.infer<typeof GeographicDataSchema>;

/**
 * Schema for geographic data list
 */
export const GeographicDataListSchema = z.array(GeographicDataSchema);

export type GeographicDataList = z.infer<typeof GeographicDataListSchema>;

// ============================================================================
// Time Series Data Schemas
// ============================================================================

/**
 * Schema for time series data point
 */
export const TimeSeriesPointSchema = z.object({
  date: z.string().datetime(),
  value: z.number().min(0),
});

export type TimeSeriesPoint = z.infer<typeof TimeSeriesPointSchema>;

/**
 * Schema for time series data
 */
export const TimeSeriesDataSchema = z.object({
  labels: z.array(z.string()),
  data: z.array(z.number()),
  title: z.string(),
  timeseries: z.boolean().default(true),
});

export type TimeSeriesData = z.infer<typeof TimeSeriesDataSchema>;

// ============================================================================
// Dashboard Response Schemas
// ============================================================================

/**
 * Schema for status counter data (from original Nuxt implementation)
 */
export const StatusCounterSchema = z.object({
  general: z.record(z.number()),
  pendings: z.record(z.number()),
  finished: z.record(z.number()),
});

export type StatusCounter = z.infer<typeof StatusCounterSchema>;

/**
 * Schema for SDK counter data
 */
export const SdkCounterItemSchema = z.object({
  version: z.string(),
  count: z.number().int().min(0),
});

export type SdkCounterItem = z.infer<typeof SdkCounterItemSchema>;

/**
 * Schema for validations by date (actual server response format)
 */
export const ValidationsByDateSchema = z.object({
  date: z.array(z.string()),
  totalData: z.array(z.number().nullable()),
  pendingData: z.array(z.number().nullable()),
  finishedData: z.array(z.number().nullable()),
  approvedData: z.array(z.number().nullable()),
  rejectedData: z.array(z.number().nullable()),
});

export type ValidationsByDate = z.infer<typeof ValidationsByDateSchema>;

/**
 * Schema for validation duration percentiles
 */
export const ValidationDurationSchema = z.array(z.object({
  name: z.string(),
  value: z.string(),
}));

export type ValidationDuration = z.infer<typeof ValidationDurationSchema>;

/**
 * Categories the server emits for the Rejection Reasons widget.
 * Mirror of `REJECTION_CATEGORIES` in
 * `wya-services-identity-verification-api/src/verifications/service/rejectionCategorizer.ts`.
 * Adding a category here without adding it server-side (or vice versa)
 * will fail the zod parse — keeping them in lockstep is intentional.
 */
export const RejectionCategorySchema = z.enum([
  'device_rate_limit',
  'device_integrity',
  'liveness_spoofing',
  'biometric_mismatch',
  'document_invalid',
  'other',
]);

export type RejectionCategory = z.infer<typeof RejectionCategorySchema>;

export const RejectionReasonEntrySchema = z.object({
  category: RejectionCategorySchema,
  count: z.number().int().min(0),
  // Percentage of all rejections in the period (not of total validations).
  percentage: z.number().min(0).max(100),
});

export type RejectionReasonEntry = z.infer<typeof RejectionReasonEntrySchema>;

/**
 * Schema for complete dashboard API response (matching actual server response)
 */
export const DashboardApiResponseSchema = z.object({
  statusCounter: z.object({
    pendings: z.record(z.number()),
    finished: z.record(z.number()),
    general: z.record(z.number()),
    total: z.number(),
    devices: z
      .object({
        unique: z.number().int().min(0),
        approved: z.number().int().min(0),
        conversionRate: z.number().min(0).max(100),
      })
      .optional(),
  }),
  validationsByDate: ValidationsByDateSchema,
  validationDuration: ValidationDurationSchema,
  devicesCounter: z.array(z.unknown()),
  browsersCounter: z.array(z.unknown()),
  androidVersionCounter: z.array(z.unknown()),
  androidSdkCounter: z.array(z.unknown()),
  geolocations: z.array(z.unknown()),
  // Optional during rollout — older API versions don't emit this. Once
  // every deployed metrics service includes the field, tighten to required.
  rejectionReasons: z.array(RejectionReasonEntrySchema).optional(),
  // Optional during rollout — older metrics buckets pre-date the SDK
  // version aggregation. Tighten once the rollout is universal.
  sdkVersionCounter: z.array(SdkVersionEntrySchema).optional(),
  // Optional during rollout — older deployments of MetricsService don't
  // emit the field. Tighten to required once every stage is on the new
  // build.
  idTypeCounter: z.array(IdTypeEntrySchema).optional(),
});

export type DashboardApiResponse = z.infer<typeof DashboardApiResponseSchema>;

// ============================================================================
// Dashboard Query Parameters Schemas
// ============================================================================

/**
 * Schema for dashboard query parameters
 */
/**
 * Cross-filter selections that the user has clicked on chart segments.
 * Mirror of `DashboardFilters` from `@/store/dashboardFiltersStore` —
 * kept here so the API layer can validate + propagate without taking
 * a runtime dep on the store module (avoids a cycle between store ↔ api).
 */
export const DashboardFiltersSchema = z.object({
  device: z.string().optional(),
  browser: z.string().optional(),
  rejectionCategory: RejectionCategorySchema.optional(),
  dateBucket: z.string().optional(),
  status: z.enum(['approved', 'rejected', 'pending']).optional(),
  country: z.string().optional(),
  // Composite "<PLATFORM>|<version>" from the SDK Version widget.
  // Must mirror `DashboardFilters.sdkVersion` in the store — Zod strips
  // unknown keys, so any new filter dimension has to be added here too.
  sdkVersion: z.string().optional(),
  // Document type identifier (e.g. "ARG_3") from the Document Type widget.
  idType: z.string().optional(),
});

export type DashboardFiltersInput = z.infer<typeof DashboardFiltersSchema>;

export const DashboardQueryParamsSchema = z.object({
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  period: z.enum(['day', 'week', 'month', 'year']).default('month'),
  timezone: z.string().optional(),
  filters: DashboardFiltersSchema.optional(),
});

export type DashboardQueryParams = z.infer<typeof DashboardQueryParamsSchema>;

// ============================================================================
// Error Schemas
// ============================================================================

/**
 * Schema for dashboard API error response
 */
export const DashboardErrorSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
    details: z.record(z.unknown()).optional(),
  }),
});

export type DashboardError = z.infer<typeof DashboardErrorSchema>;
